import { Component, Input, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Edition } from '../../models/edition.model';
import { EditionsService } from '../../services/editions.service';
import { SpacesService } from '../../services/spaces.service';

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF', SEK: 'kr',
  NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', AED: 'AED', INR: '₹'
};

@Component({
    selector: 'app-editions',
    imports: [CommonModule, TranslateModule],
    templateUrl: './editions.component.html',
    styleUrl: './editions.component.scss'
})
export class EditionsComponent implements OnInit, OnChanges {
  private readonly editionsService = inject(EditionsService);
  private readonly spacesService = inject(SpacesService);

  /** Edition IDs derived from the highlighted solution portfolio products. */
  @Input() suggestedEditionIds: string[] = [];

  showAllEditions = false;
  editions: Edition[] = [];
  selectedEditionIds = new Set<string>();
  activeCurrencyCode = 'EUR';
  private userHasCustomised = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestedEditionIds'] && !this.userHasCustomised) {
      this.selectedEditionIds = new Set(this.suggestedEditionIds);
      if (this.selectedEditionIds.size > 0) {
        this.saveEditions();
      }
    }
  }

  ngOnInit(): void {
    this.editionsService.getEditions().subscribe((editions) => {
      this.editions = editions;
    });
    this.spacesService.activeSpace$.subscribe(space => {
      this.activeCurrencyCode = space.currencyCode ?? 'EUR';
    });
    // Restore persisted edition selection (one-time on init)
    const stored = this.spacesService.getSelectedEditionIds();
    if (stored.length > 0) {
      this.selectedEditionIds = new Set(stored);
    }
  }

  formatPrice(edition: Edition): string {
    const code = this.activeCurrencyCode;
    const amount = edition.prices?.[code] ?? edition.prices?.['EUR'];
    if (amount == null) {
      return 'n/a';
    }
    const symbol = CURRENCY_SYMBOLS[code] ?? code;
    const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${formatted} ${symbol}/ month`;
  }

  toggleAllEditions(): void {
    this.showAllEditions = !this.showAllEditions;
  }

  toggleEdition(editionId: string): void {
    this.userHasCustomised = true;
    if (this.selectedEditionIds.has(editionId)) {
      this.selectedEditionIds.delete(editionId);
    } else {
      this.selectedEditionIds.add(editionId);
    }
    this.saveEditions();
  }

  private saveEditions(): void {
    this.spacesService.updateActiveSpaceEditions([...this.selectedEditionIds]);
  }

  isEditionActive(editionId: string): boolean {
    return this.selectedEditionIds.has(editionId);
  }

  get suggestedEditions(): Edition[] {
    if (this.selectedEditionIds.size === 0) {
      return [];
    }
    return this.editions.filter(e => this.selectedEditionIds.has(e.id));
  }

  /** @deprecated kept for backward compatibility if any template uses it */
  get selectedEditions(): Edition[] {
    return this.editions.filter((edition) => this.selectedEditionIds.has(edition.id));
  }
}
